import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatDate } from '../../utils/formatters.js';

import { RecurringJournalModal } from './RecurringJournalModal.jsx';

export function RecurringJournalsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState(null);

  const list = useQuery({ queryKey: ['finance', 'recurring-journals'], queryFn: () => financeApi.listRecurringJournals().then((r) => r.items) });
  const rows = list.data || [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance', 'recurring-journals'] });

  const run = useMutation({ mutationFn: (id) => financeApi.runRecurringJournal(id), onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message) });
  const toggle = useMutation({ mutationFn: ({ id, active }) => financeApi.setRecurringJournalActive(id, active), onSuccess: invalidate });
  const runDue = useMutation({ mutationFn: () => financeApi.runDueRecurring(), onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message) });

  const columns = [
    { key: 'description', label: t('rj.col.description') },
    { key: 'frequency', label: t('rj.col.frequency'), render: (r) => t(`rj.freq.${r.frequency}`) },
    { key: 'next', label: t('rj.col.next'), render: (r) => formatDate(r.nextRun) },
    { key: 'last', label: t('rj.col.last'), render: (r) => (r.lastRun ? formatDate(r.lastRun) : '-') },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={r.active ? 'success' : 'neutral'}>{r.active ? t('rj.active') : t('rj.inactive')}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => (
      <span style={{ display: 'inline-flex', gap: 'var(--space-1)' }}>
        {r.active && <Button size="sm" variant="primary" disabled={run.isPending} onClick={() => run.mutate(r.id)}>{t('rj.action.run')}</Button>}
        <Button size="sm" variant="ghost" onClick={() => toggle.mutate({ id: r.id, active: !r.active })}>{r.active ? t('rj.action.deactivate') : t('rj.action.activate')}</Button>
      </span>
    ) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rj.title')}</h1>
          <p className="muted">{t('rj.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" disabled={runDue.isPending} onClick={() => runDue.mutate()}>{t('rj.action.runDue')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('rj.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('rj.metric.active')} value={String(rows.filter((r) => r.active).length)} trendVariant="success" />
        <MetricCard label={t('rj.metric.total')} value={String(rows.length)} />
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 'var(--space-3)' }}>{error}</div>}
      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('rj.empty')} />

      <RecurringJournalModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
